import { inject, Injectable } from '@angular/core';
import { EHelpCurationStatus } from '../enums/help-curation-status.enum';
import { IHelpCurationItem } from '../interfaces/help-curation-item.interface';
import { IHelpCurationPromoteRequest } from '../interfaces/help-curation-promote-request.interface';
import { IHelpCurationRawItem } from '../interfaces/help-curation-raw-item.interface';
import { IHelpCurationRejectRequest } from '../interfaces/help-curation-reject-request.interface';
import { IHelpOdooImportStatus } from '../interfaces/help-odoo-import-status.interface';
import { IHelpOdooImportUrl } from '../interfaces/help-odoo-import-url.interface';
import { HelpApiClient } from './help-api-client.service';

interface IHelpCurationListResponse {
  status: string;
  items: IHelpCurationRawItem[];
}

@Injectable({
  providedIn: 'root',
})
export class HelpCurationService {
  private readonly helpApiClient = inject(HelpApiClient);

  public async list(status: EHelpCurationStatus): Promise<IHelpCurationItem[]> {
    const response = await this.helpApiClient.get<IHelpCurationListResponse>('/curation', { status });
    return (response.items ?? []).map((item) => this.toCurationItem(item));
  }

  public async promote(request: IHelpCurationPromoteRequest): Promise<void> {
    await this.helpApiClient.post<unknown>('/curation/promote', request);
  }

  public async reject(request: IHelpCurationRejectRequest): Promise<void> {
    await this.helpApiClient.post<unknown>('/curation/reject', request);
  }

  /** Request a presigned S3 URL to upload an Odoo helpdesk export (.xlsx). */
  public async requestOdooImportUrl(): Promise<IHelpOdooImportUrl> {
    return this.helpApiClient.post<IHelpOdooImportUrl>('/curation/odoo-import/url', {});
  }

  /** Upload the export straight to S3 with the presigned URL (no auth header — the URL is signed). */
  public async uploadOdooExport(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, { method: 'PUT', body: file });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
  }

  public async getOdooImportStatus(importId: string): Promise<IHelpOdooImportStatus> {
    return this.helpApiClient.get<IHelpOdooImportStatus>('/curation/odoo-import/status', { importId });
  }

  private toCurationItem(item: IHelpCurationRawItem): IHelpCurationItem {
    return {
      sessionId: item.session_id,
      turnId: item.turn_id,
      question: item.question,
      answer: item.answer,
      locale: item.locale,
      curationStatus: item.curation_status as EHelpCurationStatus,
      feedbackHelpful: item.feedback_helpful,
      feedbackComment: item.feedback_comment ?? undefined,
      feedbackAt: item.feedback_at,
      rejectedReason: item.rejected_reason ?? undefined,
    };
  }
}
